import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import type { UserDocumentsDto } from "../../../../api";
import { DocumentDtoStatusEnum } from "../../../../api";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";

// A banking change request can also be CHANGES_REQUESTED, which is still
// awaiting action. Without mapping it onto Pending it counted toward Total but
// appeared in no status column, so the row's columns did not add up.
const PENDING_CR_STATUSES = new Set(["PENDING", "CHANGES_REQUESTED"]);

function countByStatus(row: UserDocumentsDto, status: string) {
  const docCount = row.documents?.filter((d) => d.status === status).length ?? 0;
  const bankingCount =
    row.bankingChangeRequests?.filter((cr) =>
      status === DocumentDtoStatusEnum.Pending
        ? PENDING_CR_STATUSES.has(cr.status)
        : cr.status === status,
    ).length ?? 0;
  return docCount + bankingCount;
}

function totalCount(row: UserDocumentsDto) {
  return (row.documents?.length ?? 0) + (row.bankingChangeRequests?.length ?? 0);
}

export function buildAdminDocumentReviewColumns(
  onView: (userId: string) => void,
): SimpleTableColumn<UserDocumentsDto>[] {
  return [
    {
      key: "partner",
      label: "Partner Name",
      className: "flex-[2] min-w-0",
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <Text variant="bodyBold" tone="dark">{row.userName || "—"}</Text>
          {row.userEmail && <Text variant="bodySm" tone="muted">{row.userEmail}</Text>}
        </div>
      ),
    },
    {
      key: "total",
      label: "Total",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{totalCount(row)}</Text>,
    },
    {
      key: "pending",
      label: "Pending",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">{countByStatus(row, DocumentDtoStatusEnum.Pending)}</Text>
      ),
    },
    {
      key: "approved",
      label: "Approved",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">{countByStatus(row, DocumentDtoStatusEnum.Approved)}</Text>
      ),
    },
    {
      key: "rejected",
      label: "Rejected",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">{countByStatus(row, DocumentDtoStatusEnum.Rejected)}</Text>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-1 flex justify-end",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); onView(row.userId); }}
        >
          View
        </Button>
      ),
    },
  ];
}
